import React, { useCallback, useMemo, useState } from 'react';
import { SelectChangeEvent } from '@mui/material/Select';
import { useIntl } from 'react-intl';
import Paper from '@mui/material/Paper';
import makeStyles from '@mui/styles/makeStyles';
import { useGetMyAnalyticsData } from 'modules/Insights/Notes/hooks/useGetMyAnalyticsData';
import { MyAnalyticsDimensionType } from 'graphql/types';
import { DropdownLabel, transformAnalyticsData } from './util';
import Legend from './Legend';

import SourceByTimeChart from './SourceByTimeChart';

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    padding: theme.spacing(2),
    marginTop: theme.spacing(2)
  }
}));

export type DatasetProp = {
  x: string;
  y: number;
  name: string;
  value: number;
  color: string;
  imgSrc: string | undefined;
  id?: string;
};

export type DataValueProp = {
  dataset: DatasetProp[][];
  loading: boolean;
  isEmpty: boolean;
};

const UserSourceByTimeChart: React.FC = () => {
  const { formatMessage } = useIntl();
  const classes = useStyles();

  const manualEntries = formatMessage({
    id: 'insights.timeBreakdownChart.manualEntries'
  });
  const [selectvalue, setSelectValue] = useState<string>(
    DropdownLabel.collectors
  );

  const onSelectValueChange = useCallback((event: SelectChangeEvent) => {
    setSelectValue(event.target.value);
  }, []);

  const isMetricsByCollectors = useMemo(() => {
    return selectvalue === DropdownLabel.collectors;
  }, [selectvalue]);

  const { data, loading, isEmpty } = useGetMyAnalyticsData(
    isMetricsByCollectors
      ? MyAnalyticsDimensionType.MetricsBySource
      : MyAnalyticsDimensionType.MetricsByCategory
  );
  const dataset = useMemo(() => {
    if (isEmpty) {
      return [];
    } else
      return transformAnalyticsData(data?.myAnalytics?.items, manualEntries);
  }, [data?.myAnalytics?.items, isEmpty, manualEntries]);

  const isDisabled = useMemo(() => {
    return Boolean((isEmpty && isMetricsByCollectors) || loading);
  }, [isEmpty, isMetricsByCollectors, loading]);

  const dataValues = useMemo(() => {
    return {
      dataset: dataset,
      loading: loading,
      isEmpty: isEmpty
    };
  }, [dataset, isEmpty, loading]);

  return (
    <Paper
      className={classes.container}
      elevation={3}
      data-testid={'source-by-time-chart-container'}
      tabIndex={0}
    >
      <SourceByTimeChart
        isDisabled={isDisabled}
        dataValues={dataValues}
        selectvalue={selectvalue}
        isMetricsByCollectors={isMetricsByCollectors}
        onSelectValueChange={onSelectValueChange}
      />
      {!isEmpty && !loading && (
        <Legend
          dataset={dataValues.dataset}
          isMetricsByCollectors={isMetricsByCollectors}
        />
      )}
    </Paper>
  );
};

export default React.memo(UserSourceByTimeChart);
